import { useState } from 'react'
import { Button } from '@components/primitives/Button.js'
import { Modal } from '@components/primitives/Modal.js'
import { Input } from '@components/primitives/Input.js'
import { MoneyInput } from '@components/primitives/MoneyInput.js'
import { Select } from '@components/primitives/Select.js'
import { Textarea } from '@components/primitives/Textarea.js'
import {
  CATEGORIAS_FINANCEIRAS,
  FORMAS_PAGAMENTO,
  STATUS_LANCAMENTO,
  MODULOS,
} from '@utils/constants.js'
import type { FormState } from './financeiroTypes.js'

type Props = {
  open: boolean
  initial: FormState
  title: string
  onSave: (data: FormState) => Promise<void>
  onClose: () => void
}

export function LancamentoFormModal({ open, initial, title, onSave, onClose }: Props) {
  const [form, setForm] = useState<FormState>(initial)
  const [saving, setSaving] = useState(false)

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((prev) => ({ ...prev, [k]: v }))

  const isReceita = form.tipo === 'receita'
  const canSubmit = Boolean(form.titulo.trim() && Number(form.valor) > 0 && form.data)

  const handleSubmit = async () => {
    if (!canSubmit) return
    setSaving(true)
    await onSave(form)
    setSaving(false)
  }

  const moduloOptions = [
    { value: '', label: 'Nenhum' },
    ...MODULOS.map((m) => ({ value: m.id, label: m.titulo })),
  ]

  return (
    <Modal
      open={open}
      title={title}
      size='md'
      onClose={onClose}
      actions={
        <div className='cf3-modal-actions'>
          <Button variant='ghost' onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button
            variant='primary'
            onClick={handleSubmit}
            loading={saving}
            disabled={!canSubmit}
            aria-label='Salvar lançamento'
          >
            Salvar
          </Button>
        </div>
      }
    >
      <div className='cf3-fin-form'>
        <Input
          label='Descrição'
          value={form.titulo}
          placeholder={isReceita ? 'Ex: Financiamento liberado' : 'Ex: Compra de tijolos'}
          onChange={(e) => set('titulo', e.target.value)}
        />

        <div className='cf3-fin-form__row'>
          <MoneyInput
            label='Valor'
            value={Number(form.valor) || 0}
            onChange={(v) => set('valor', String(v))}
          />
          <Input
            label='Data'
            type='date'
            value={form.data}
            onChange={(e) => set('data', e.target.value)}
          />
        </div>

        {!isReceita && (
          <>
            <div>
              <span className='cf3-chip-label'>Categoria</span>
              <div className='cf3-chip-grid' role='group' aria-label='Categoria'>
                {CATEGORIAS_FINANCEIRAS.map((c) => (
                  <button
                    key={c}
                    type='button'
                    className={`cf3-chip${form.categoria === c ? ' cf3-chip--active' : ''}`}
                    onClick={() => set('categoria', c)}
                    aria-pressed={form.categoria === c}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>

            <Input
              label='Fornecedor / Prestador'
              value={form.fornecedor}
              placeholder='Ex: Depósito São João'
              onChange={(e) => set('fornecedor', e.target.value)}
            />
          </>
        )}

        <div>
          <span className='cf3-chip-label'>Forma de Pagamento</span>
          <div className='cf3-chip-grid' role='group' aria-label='Forma de pagamento'>
            {FORMAS_PAGAMENTO.map((p) => (
              <button
                key={p.value}
                type='button'
                className={`cf3-chip${form.formaPagamento === p.value ? ' cf3-chip--active' : ''}`}
                onClick={() => set('formaPagamento', p.value)}
                aria-pressed={form.formaPagamento === p.value}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {!isReceita && (
          <Select
            label='Status'
            value={form.status}
            options={STATUS_LANCAMENTO}
            onChange={(e) => set('status', e.target.value)}
          />
        )}

        <Select
          label='Módulo vinculado'
          value={form.moduloId}
          options={moduloOptions}
          onChange={(e) => set('moduloId', e.target.value)}
        />

        <Textarea
          label='Observações (opcional)'
          value={form.observacoes}
          rows={2}
          placeholder='Informações adicionais…'
          onChange={(e) => set('observacoes', e.target.value)}
        />
      </div>
    </Modal>
  )
}
